import { useState } from 'react'
import { useNavigate } from 'react-router'
import { errorMessage } from '../../api/client'
import { api } from '../../api/endpoints'
import type { User } from '../../api/types'
import { Dialog } from '../../components/Dialog'
import { useSession } from '../../state/session'
import { useUi } from '../../state/ui'
import { storeNewChannel } from './createConversation'
import { nextOnEnter } from './formKeys'
import { UserPicker } from './UserPicker'

export function NewGroupDialog() {
  const open = useUi((s) => s.dialog === 'new-group')
  const close = useUi((s) => s.closeDialog)
  return open ? <NewGroupForm onClose={close} /> : null
}

/**
 * A group chat is a place staff make for themselves, so it is named before it exists: the cursor starts in the name,
 * and the people come after. A channel is the official counterpart, which only an administrator sets up.
 */
function NewGroupForm({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const meId = useSession((s) => s.me?.id)
  const [name, setName] = useState('')
  const [members, setMembers] = useState<User[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const trimmed = name.trim()
  const ready = Boolean(trimmed) && members.length > 0

  const submit = async () => {
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      const summary = await api.createDm(
        members.map((m) => m.id),
        trimmed,
      )
      await storeNewChannel(summary)
      onClose()
      navigate(`/c/${summary.id}`)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      chrome="bar"
      open
      onClose={onClose}
      title="New group"
      titleId="new-group-title"
      description="A group chat for a few people, named so everyone knows what it is for."
      className="new-dialog"
      action={
        <button type="submit" form="new-group-form" className="bar-button bar-action" aria-label="Create group" disabled={busy || !ready}>
          Create
        </button>
      }
    >
      <form
        id="new-group-form"
        style={{ display: 'contents' }}
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <div className="dialog-body">
          {error ? (
            <p role="alert" className="field-error form-error">
              {error}
            </p>
          ) : null}
          <div className="field">
            <label className="field-label" htmlFor="group-name">
              Group name
            </label>
            <input
              id="group-name"
              data-autofocus
              className="text-input"
              value={name}
              maxLength={80}
              placeholder="e.g. Weekend cover"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={nextOnEnter}
              enterKeyHint="next"
              autoComplete="off"
              required
            />
          </div>
          <div className="field">
            <span className="field-label">Add people</span>
            <p className="field-help" style={{ marginTop: 0, marginBottom: 10 }}>
              You're in it automatically. Anyone in a group can add more people later. A group chat is between people; bots belong in channels.
            </p>
            <UserPicker selected={members} onChange={setMembers} excludeIds={meId ? [meId] : []} label="Search staff" />
          </div>
        </div>
      </form>
    </Dialog>
  )
}
